import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  OutlinedInput,
} from "@mui/material";
import "../../styles/inputBox.css";
import WorkIcon from "../../assets/images/JobIcon/work-icon.svg";
import OverviewIcon from "../../assets/images/JobIcon/Overview.svg";
import GoalIcon from "../../assets/images/JobIcon/goal.svg";
import CheckIcon from "../../assets/images/JobIcon/check-bold.svg";
import SymbolsIcon from "../../assets/images/JobIcon/symbols_step.svg";
import TeamsIcon from "../../assets/images/JobIcon/team-people.svg";
import { useNavigate, useParams } from "react-router-dom";
import { getRequest, postRequest, putRequest } from "../../api/apiServices";
import {
  changeJobSummaryStatus,
  createJob,
  createJobSummary,
  createJobVacancyText,
  getJobAndCompanyData,
  getJobSummaryByJobId,
  updateJob,
  updateJobSummaryByJobId,
} from "../../api/apiList";
import { generateText } from "../../utils/AIGenerateText";
import Loader from "../../utils/Loading";
import Toast from "../../utils/Toast";
import { roleBasedRedirection } from "../../utils/helper";

interface JobAndCompanyData {
  name: string;
  companyVisionAndMission: string;
  employees: string;
  industry: string;
  services: string;
  employerValueProposition: string;
  companyMileStones: string;
  companyCulture: string;
  jobTitle: string;
  vacancyReason: string;
  positionGoals: string;
  roleChallenges: string;
  taskOfThisPosition: string;
  anyProjectsOfCandidates: string;
  teamInteraction: string;
  reportingStructure: string;
  cultureFit: string;
  educationAndExperience: string;
  technicalSkillsRequired: string;
  softSkillsRequired: string;
  preferredCertificationsLicenses: string;
  idealCandidateProfile: string;
  idealExperienceLevel: string;
  industrySpecificExperienceRequired: string;
  secondaryBenefits: string;
  salaryRange: string;
  benefitsWithThisRole: string;
  organizationalGrowthOpportunities: string;
}

const JobSummary = () => {
  const { id } = useParams();
  const [jobAndCompanyData, setJobAndCompanyData] =
    useState<JobAndCompanyData | null>(null);
  const [jobSummariesId, setJobSummariesId] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [hiringManagerId, setHiringManagerId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [overview, setOverview] = useState("");
  const [responsibilities, setResponsibilities] = useState("");
  const [challenges, setChallenges] = useState("");
  const [team, setTeam] = useState("");
  const [interview, setInterview] = useState("");
  const [isAccepted, setIsAccepted] = useState("");
  const [professionalData, setProfessionalData] = useState("");
  const [neutralData, setNeutralData] = useState("");
  const [engagingData, setEngagingData] = useState("");
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");
  const navigate = useNavigate();

  //OnLoad Set the Data of Job Summery
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response: any = await getRequest(getJobSummaryByJobId(id));
      setLoading(false);
      if (response) {
        const data = response?.data?.data;
        setJobSummariesId(data.id);
        setJobTitle(data.jobTitle);
        setHiringManagerId(data.hiringManagerId);
        setOverview(data.overview);
        setResponsibilities(data.responsibilities);
        setChallenges(data.challenges);
        setTeam(data.team);
        setInterview(data.interview);
        setIsAccepted(data.isAccepted);
        setCompanyId(data.companyId);

        if (!data.isAccepted) {
          fetchJobAndCompany(data.jobId, data.companyId);
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchJobAndCompany = async (jobId: string, companyId: string) => {
    const data = {
      jobId: jobId,
      companyId: companyId,
    };

    const response = await postRequest(getJobAndCompanyData, data);
    if (response?.data.success) {
      setJobAndCompanyData(response.data.data);
    }
  };

  const handleAcceptClick = async () => {
    try {
      const data = {
        isAccepted: true,
      };
      setLoading(true);

      const response = await putRequest(changeJobSummaryStatus(id), data);
      setLoading(false);
      if (response) {
        handleSubmit();
        
       await handleCreateJobVacancy(jobAndCompanyData as JobAndCompanyData);

        navigate(roleBasedRedirection("/job-summary"));
      }
    } catch (error) {
      setLoading(false);
      console.error("Error updating job summary:", error);
    }
  };

  const handleSubmit = async () => {
    const data = {
      jobId: id,
      hiringManagerId,
      overview,
      responsibilities,
      challenges,
      team,
      interview,
      companyId: companyId,
    };
    setLoading(true);
    const response = await putRequest(updateJobSummaryByJobId(id), data);
    setLoading(false);
    if (response) {
      Toast(
        response?.data?.statusMessage || "Job Summary Updated Successfully"
      );
      navigate(roleBasedRedirection("/job-summary"));
    }
  };

  // Function to create job vacancy
  const handleCreateJobVacancy = async (
    jobAndCompanyData: JobAndCompanyData
  ) => {
    const data = {
      jobSummariesId,
      jobId: id,
      companyId: companyId,
      jobAndCompanyData: jobAndCompanyData,
    };
    await postRequest(createJobVacancyText, data);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <div>
        <Container
          style={{
            display: "flex",
            padding: "25px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            spacing={2}
            bgcolor="#FFFFFF"
            width="884px"
            borderRadius="22px"
            border="1px solid #D6D6D7"
            margin={0}
          >
            <Grid
              item
              height={isMobile ? "auto" : "auto"}
              style={{ padding: "23px 16px" }}
            >
              <Grid
                container
                display="flex"
                bgcolor="#84E1CC"
                borderRadius="17px"
                padding="18px"
              >
                <Grid item display="flex" gap="20px">
                  <img src={WorkIcon} alt="team-icon" />
                  <Grid display="flex" alignItems="center">
                    <Typography
                      color="#FFFFFF"
                      fontWeight={700}
                      fontSize="20.94px"
                    >
                      {jobTitle}
                    </Typography>
                    <Grid mb="1px"></Grid>
                    <Typography
                      color="#FFFFFF"
                      fontWeight={500}
                      fontSize="12.94px"
                    ></Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid mt="35px"></Grid>
              <Grid
                display="flex"
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                gap="10px"
              >
                <Grid>
                  <Box
                    padding="17.50px"
                    borderRadius="7px"
                    width="fit-content"
                    bgcolor="#84E1CC"
                  >
                    <img src={OverviewIcon} width="32px" height="32px" />
                  </Box>
                </Grid>
                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={3}
                  maxRows={10}
                  className="text-area-size"
                  value={overview}
                  onChange={(e) => {
                    const value = e.target.value;
                    setOverview(value);
                  }}
                />
              </Grid>
              <Grid mt="61px"></Grid>
              <Grid
                display="flex"
                alignItems="center"
                direction={isMobile ? "column" : "row"}
                gap="10px"
              >
                <Grid>
                  <Box
                    padding="17.50px"
                    borderRadius="7px"
                    width="fit-content"
                    bgcolor="#84E1CC"
                  >
                    <img src={CheckIcon} width="32px" height="32px" />
                  </Box>
                </Grid>
                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={3}
                  maxRows={10}
                  className="text-area-size"
                  value={responsibilities}
                  onChange={(e) => {
                    const value = e.target.value;
                    setResponsibilities(value);
                  }}
                />
              </Grid>
              <Grid mt="61px"></Grid>
              <Grid
                display="flex"
                alignItems="center"
                direction={isMobile ? "column" : "row"}
                gap="10px"
              >
                <Grid>
                  <Box
                    padding="17.50px"
                    borderRadius="7px"
                    width="fit-content"
                    bgcolor="#84E1CC"
                  >
                    <img src={GoalIcon} width="32px" height="32px" />
                  </Box>
                </Grid>

                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={3}
                  maxRows={10}
                  className="text-area-size"
                  value={challenges}
                  onChange={(e) => {
                    const value = e.target.value;
                    setChallenges(value);
                  }}
                />
              </Grid>
              <Grid mt="61px"></Grid>
              <Grid
                display="flex"
                alignItems="center"
                direction={isMobile ? "column" : "row"}
                gap="10px"
              >
                <Grid>
                  <Box
                    padding="17.50px"
                    borderRadius="7px"
                    width="fit-content"
                    bgcolor="#84E1CC"
                  >
                    <img src={TeamsIcon} width="32px" height="32px" />
                  </Box>
                </Grid>
                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={3}
                  maxRows={10}
                  className="text-area-size"
                  value={team}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTeam(value);
                  }}
                />
              </Grid>
              <Grid mt="61px"></Grid>
              <Grid
                display="flex"
                alignItems="center"
                direction={isMobile ? "column" : "row"}
                gap="10px"
              >
                <Grid>
                  <Box
                    padding="17.50px"
                    borderRadius="7px"
                    width="fit-content"
                    bgcolor="#84E1CC"
                  >
                    <img src={SymbolsIcon} width="32px" height="32px" />
                  </Box>
                </Grid>
                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={3}
                  maxRows={10}
                  className="text-area-size"
                  value={interview}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInterview(value);
                  }}
                />
              </Grid>
              <Grid mt="43px"></Grid>
              <Grid
                container
                direction={isMobile ? "column" : "row"}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                padding={isMobile ? "" : "0px 34px 0px 34px"}
                gap={isMobile ? "10px" : ""}
              >
                <Grid>
                  <button
                    type="submit"
                    className="user-scale"
                    style={{
                      backgroundColor: "#84E1CC",
                      boxShadow: "none",
                      borderRadius: "5px",
                      padding: isTabs ? "19px 55px" : "17px 17px",
                      lineHeight: "0px",
                      letterSpacing: "0px",
                      textTransform: "none",
                      border: "none",
                      color: "white",
                      width: " 100%",
                    }}
                    onClick={isAccepted ? handleSubmit : handleAcceptClick}
                  >
                    {isAccepted ? "Update Job Summary" : "Accept Job Summary"}
                  </button>
                </Grid>
              </Grid>
              <Grid mb="10px"></Grid>
            </Grid>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default JobSummary;
